import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import {
  fireEvent,
  inStorage,
  removeOutDated,
  storeModalState,
} from 'common/js/salesforce/helpers';
import { IS_ONE_SEALINK } from 'utils/constant';
import Modal from 'components/modal_wrapper';
import { getCurrentBooking } from 'redux/selectors/current_booking';
import { getFirstFerryGrouping } from 'redux/selectors/ui/sealink';
import {
  buildPayload,
  onOverviewOrCheckout,
  requestToSalesforce,
} from './helpers';

const EVENT_NAME = 'abandonedCart';

const AbandonCartModal = ({
  abandonCartForm: formHandle,
  cartAbandonmentModalTitle,
  cartAbandonmentModalContent,
  subscribeBtn,
  snapshotTest,
}) => {
  const history = useHistory();
  const [show, setShow] = useState(false);
  const [subscribed, setSubscribed] = useState(true);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const booking = useSelector(getCurrentBooking);
  const dialogState = useSelector((state) => state.checkout?.dialogState);
  const { checkoutForm = {} } = useSelector(getFirstFerryGrouping) || {};
  const { email: emailFromCheckout } = checkoutForm;

  const isPaying = dialogState !== 0;

  const {
    id: bookingId,
    balance_in_cents: balanceInCents,
    customer_contact_email: customerContactEmail,
  } = booking;
  const hasUnpaidBooking = balanceInCents !== undefined && balanceInCents > 0;

  const redirectText = onOverviewOrCheckout()
    ? 'Continue with booking'
    : 'Return to booking';
  const onHide = () => {
    // When user close the modal, remember the booking id to prevent showing the modal again.
    storeModalState(bookingId);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const formData = Object.fromEntries(new FormData(e.target));
    const { emailAddress, subscribe } = formData;

    if (emailAddress) {
      const { payload, accountRelated } = buildPayload({
        emailFromForm: emailAddress,
        subscribe,
        booking,
        checkoutForm,
      });

      fireEvent(EVENT_NAME, payload);
      requestToSalesforce({ accountRelated, formHandle });
      setSubmitted(true);
    }
  };

  const onRedirect = () => {
    setShow(false);
    // When user close the modal, remember the booking id to prevent showing the modal again.
    storeModalState(bookingId);
    if (onOverviewOrCheckout()) {
      // Do nothing if user is on overview or checkout page
      return;
    }
    if (IS_ONE_SEALINK) {
      if (history.location.pathname.includes('/bookings')) {
        history.push('/checkout');
      } else {
        window.location.href = `${ecom.craftConfig.api_prefix}/bookings`;
      }
    } else {
      window.location.href = '/cart';
    }
  };

  const mouseEvent = useCallback(
    (e) => {
      // On-exit modal only shows up when user move the cursor out of window from top.
      const shouldShowExitIntent =
        !e.toElement && !e.relatedTarget && e.clientY < 10;

      if (
        shouldShowExitIntent &&
        hasUnpaidBooking &&
        !isPaying &&
        !inStorage(bookingId)
      ) {
        setShow(true);
        setEmail(emailFromCheckout || customerContactEmail || '');
        document.removeEventListener('mouseout', mouseEvent);
      }
    },
    [
      bookingId,
      customerContactEmail,
      emailFromCheckout,
      hasUnpaidBooking,
      isPaying,
    ]
  );

  useEffect(() => {
    removeOutDated();
  }, []);

  useEffect(() => {
    document.addEventListener('mouseout', mouseEvent);
    return () => {
      document.removeEventListener('mouseout', mouseEvent);
    };
  }, [mouseEvent]);

  const display = show || snapshotTest;
  return (
    <Modal
      id="abandon-cart-modal"
      show={display}
      centered
      dialogClassName="abandon-cart-modal abandon-cart-modal--dialog"
      onHide={onHide}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <h5>{cartAbandonmentModalTitle || 'Are you leaving?'}</h5>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4">
          {parse(
            cartAbandonmentModalContent ||
              "We noticed that you have left items in your cart. Leave your email, and we'll send these straight to your inbox, so you can complete your purchase when you're ready."
          )}
        </div>
        <form onSubmit={onSubmit}>
          {subscribeBtn && (
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                name="subscribe"
                id="subscribe"
                checked={subscribed}
                onChange={(e) => setSubscribed(e.target.checked)}
              />
              <label htmlFor="subscribe">
                I wish to subscribe to marketing emails
              </label>
            </div>
          )}
          <div className="fw-bold mt-3 mb-1">Enter your email</div>
          <div className="row gx-3">
            <div className="col-8">
              <label htmlFor="emailAddress" className="visually-hidden">
                EmailAddress
              </label>
              <input
                required
                type="email"
                className="form-control"
                name="emailAddress"
                id="emailAddress"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="col-4">
              <div className="d-grid">
                <button
                  type="submit"
                  className={`btn ${
                    submitted
                      ? 'btn-outline-success disabled'
                      : 'btn-outline-secondary'
                  }`}
                >
                  {submitted ? 'Success' : 'Email me'}
                </button>
              </div>
            </div>
          </div>
        </form>
        <div className="d-grid mt-3">
          <button
            type="button"
            className="btn btn-primary"
            onClick={onRedirect}
          >
            {redirectText}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AbandonCartModal;

AbandonCartModal.propTypes = {
  abandonCartForm: PropTypes.string,
  cartAbandonmentModalContent: PropTypes.string,
  cartAbandonmentModalTitle: PropTypes.string,
  subscribeBtn: PropTypes.bool,
  snapshotTest: PropTypes.bool,
};
